import { logger } from "./logger";
import { captureErrorAsync } from "../utils/capture-error-async";
import { analyticsTrack } from "../utils/analytics";
import { addListener } from "../utils/event-listeners";
import { analyticsReady } from "../utils/ready";

function namespaced(item: string) {
  return `formData.${item}`;
}

function traitsFromSerializedContactForm(serialized: { [k: string]: string }) {
  const traits: { [k: string]: string } = {};

  function extract(key: string) {
    return serialized[namespaced(key)];
  }

  if (extract("E-mail Address")) {
    traits.email = extract("E-mail Address");
  }

  if (extract("First Name")) {
    traits.firstName = extract("First Name");
  }

  if (extract("Last Name")) {
    traits.lastName = extract("Last Name");
  }

  if (extract("Home Postal Code")) {
    traits.postalCode = extract("Home Postal Code");
  }

  if (extract("Home Phone")) {
    traits.phoneNumber = extract("Home Phone");
  }

  return traits;
}

function formToJson(form: HTMLFormElement): { [key: string]: string } {
  const result = {};

  const formData = new FormData(form);

  formData.forEach((value, key) => {
    if (result[namespaced(key)]) {
      if (typeof result[namespaced(key)] === "string") {
        result[namespaced(key)] = [result[namespaced(key)]];
      }
      result[namespaced(key)].push(value);
    } else {
      result[namespaced(key)] = value;
    }
  });

  return result;
}

function setupPolyfills() {
  if (
    typeof Blob !== "undefined" &&
    // @ts-ignore
    (typeof FormData === "undefined" || !FormData.prototype.keys)
  ) {
    return import("formdata-polyfill");
  }
}

export function trackForm(
  selector: string,
  eventName: string,
  vars: { timeout?: number; retailerId?: string }
): void {
  const submitBtn = document.querySelector<HTMLButtonElement>(
    `${selector} input[name="Submit"]`
  );

  if (submitBtn == null) {
    logger.debug("Could not find submit button");
    return;
  }

  setupPolyfills();

  logger.debug("Attaching form submission listener to target", submitBtn);

  addListener(submitBtn, "click", async () => {
    try {
      const form = document.querySelector<HTMLFormElement>(selector);

      if (form == null) {
        return;
      }

      const serialized = formToJson(form);
      serialized["application"] = "acton";

      if (vars.retailerId) {
        serialized.retailerId = vars.retailerId;
      }

      await analyticsReady();

      try {
        logger.debug(`Tracking event (${eventName})`, serialized);
        analyticsTrack(eventName, serialized);
      } catch (e) {
        captureErrorAsync(e);
        logger.error("Could not track analytics event for submission", e);
      }

      try {
        const traits = traitsFromSerializedContactForm(serialized);

        logger.debug(`Identifying user`, traits);
        window.analytics.identify(traits, window.analytics._defaultContext);
      } catch (e) {
        captureErrorAsync(e);
        logger.error("Could not identity analytics user for submission", e);
      }
    } catch (e) {
      captureErrorAsync(e);
      logger.error("Could not properly serialize form", e);
    }
  });
}
